import { io, Socket } from "socket.io-client"
import { missionsApi } from "store/apis/missionsAPI/missions.api"
import { balanceApi, balanceEndpoints } from "store/apis/balanceAPI/balance.api"
import { IResPag } from "utils/types"
import { getSocketsUrl } from "utils/getSocketsUrl"
import { TAGS, api } from "../../api"
import { elixirsAPI } from "../elixirsAPI/elixirs.api"
import {
  AUCTION_SOCKETS,
  IAuctionCurrent,
  IAuctionSubmitBidDto,
  IAuctionSubmitBidRes,
  IAuctionBuyNowRes,
  IAuctionBuyNowDto,
  IAuctionCurrentLot,
  IAuctionSubmitBidEvent,
  IAuctionUpdatedEvent,
  AUCTION_STATUS,
  IAuctionItemSoldEvent,
  IAuctionUserStatsRes
} from "./types"

const socketName = "auction"

export const socketAuction = {
  instance: null as Socket | null,
  dispatch: null as ((action: any) => void) | null,
  updateAuctionData: null as
    | ((updater: (draft: IAuctionCurrent) => void) => void)
    | null,

  _subs: 0,
  _boundEvents: new Set<string>(),

  init(userId: string) {
    if (this.instance) return

    const auth = userId ? { userId } : undefined
    this.instance = io(`${getSocketsUrl()}/auction`, {
      auth,
      transports: ["websocket"]
    })

    this.instance.on("connect", () => {
      console.log(`Socket "${socketName}" connected`)
    })
    this.instance.on("connect_error", (error) => {
      console.error(`Socket "${socketName}" connection error:`, error)
    })
    this.instance.on("disconnect", (reason) => {
      console.warn(`Socket "${socketName}" disconnected:`, reason)
    })
    this.instance.on("reconnect", (attemptNumber) => {
      console.log(
        `Socket "${socketName}" reconnected after ${attemptNumber} attempts`
      )
    })
  },

  setUpdateAuctionData(
    updateCachedData: (updater: (draft: IAuctionCurrent) => void) => void
  ) {
    this.updateAuctionData = updateCachedData
  },

  setDispatch(dispatch: (action: any) => void) {
    this.dispatch = dispatch
  },

  addSub() {
    this._subs += 1
  },

  removeSub() {
    this._subs = Math.max(0, this._subs - 1)
    if (this._subs === 0) {
      try {
        if (this.instance) {
          this._boundEvents.forEach((evt) =>
            this.instance!.removeAllListeners(evt)
          )
        }
        this._boundEvents.clear()
        this.instance?.disconnect()
      } finally {
        this.instance = null
      }
    }
  },

  _bind(eventName: string, handler: (payload: any) => void) {
    if (!this.instance) return () => {}
    if (this._boundEvents.has(eventName)) return () => {}

    this.instance.on(eventName, handler)
    this._boundEvents.add(eventName)

    return () => {
      try {
        this.instance?.off(eventName, handler)
      } finally {
        this._boundEvents.delete(eventName)
      }
    }
  },

  bindSubmitBidListener() {
    const handler = (event: any) => {
      const eventData = JSON.parse(event) as IAuctionSubmitBidEvent
      const { lotId } = eventData

      let wasLeading = false

      this.updateAuctionData?.((draft: IAuctionCurrent) => {
        const idx = draft.lots.findIndex((row) => {
          return String(row.lot._id) === String(lotId)
        })

        if (idx === -1) {
          return
        }

        wasLeading = Boolean(draft.lots[idx].lot.lastBidder)

        draft.lots[idx].lot = {
          ...draft.lots[idx].lot,
          currentPrice: {
            gold: eventData.amountInGoldEquivalent,
            labr: eventData.amountInLabrEquivalent
          }
        }
      })

      if (wasLeading) {
        this.dispatch?.(
          auctionAPI.util.invalidateTags([
            { type: TAGS.AUCTION, id: "CURRENT" }
          ])
        )
        this.dispatch?.(balanceApi.util.invalidateTags([TAGS.BALANCE]))
      }
    }
    return this._bind(AUCTION_SOCKETS.SUBMIT_BID, handler)
  },

  bindItemSoldListener() {
    const handler = (event: any) => {
      const eventData = JSON.parse(event) as IAuctionItemSoldEvent
      const { lotId, availableCountToPurchaseNow } = eventData

      this.updateAuctionData?.((draft: IAuctionCurrent) => {
        const idx = draft.lots.findIndex((row) => {
          return String(row.lot._id) === String(lotId)
        })

        if (idx === -1) {
          return
        }

        draft.lots[idx].lot = {
          ...draft.lots[idx].lot,
          availableCountToPurchaseNow
        }
      })
    }
    return this._bind(AUCTION_SOCKETS.ITEM_SOLD, handler)
  },

  bindAuctionUpdatedListener() {
    const handler = (event: any) => {
      const eventData = JSON.parse(event) as IAuctionUpdatedEvent
      const { newStatus } = eventData

      this.updateAuctionData?.((draft: IAuctionCurrent) => {
        draft.auction!.status = newStatus
      })

      if (!this.dispatch) return

      if (newStatus === AUCTION_STATUS.ACTIVE_WARMUP) {
        this.dispatch(
          auctionAPI.util.invalidateTags([
            { type: TAGS.AUCTION, id: "CURRENT" }
          ])
        )
      }

      if (newStatus === AUCTION_STATUS.FINISHED) {
        this.dispatch(
          auctionAPI.util.invalidateTags([
            { type: TAGS.AUCTION, id: "CURRENT" }
          ])
        )
        this.dispatch(
          elixirsAPI.util.invalidateTags([{ type: TAGS.ELIXIRS, id: "LIST" }])
        )
      }
    }
    return this._bind(AUCTION_SOCKETS.AUCTION_UPDATED, handler)
  }
}

export const auctionAPI = api.enhanceEndpoints({
  addTagTypes: [TAGS.AUCTION, TAGS.MISSIONS]
})

const auctionEndpoints = auctionAPI.injectEndpoints({
  endpoints: (builder) => ({
    getAuctionCurrent: builder.query<IAuctionCurrent, { userId: string }>({
      query: () => ({
        url: `/auction/current`,
        method: "GET"
      }),
      providesTags: [{ type: TAGS.AUCTION, id: `CURRENT` }],
      async onCacheEntryAdded(
        { userId },
        { updateCachedData, cacheEntryRemoved, dispatch }
      ) {
        socketAuction.setUpdateAuctionData(updateCachedData)
        socketAuction.setDispatch(dispatch)

        socketAuction.init(userId)
        socketAuction.addSub()
        const unsubscribeSubmitBid = socketAuction.bindSubmitBidListener()
        const unsubscribeAuctionUpdated =
          socketAuction.bindAuctionUpdatedListener()
        const unsubscribeItemSold = socketAuction.bindItemSoldListener()

        try {
          await cacheEntryRemoved
        } finally {
          unsubscribeSubmitBid?.()
          unsubscribeAuctionUpdated?.()
          unsubscribeItemSold?.()
          socketAuction.removeSub()
        }
      }
    }),

    getAuctionHistory: builder.query<
      IResPag<IAuctionCurrent>,
      { page: number; limit: number }
    >({
      query: ({ page, limit }) => ({
        url: `/auction/history`,
        method: "GET",
        params: { page, limit }
      }),
      providesTags: (_, __, { page, limit }) => [
        { type: TAGS.AUCTION, id: `HISTORY` },
        {
          type: TAGS.AUCTION,
          id: `HISTORY:page:${page}:limit:${limit}`
        }
      ]
    }),

    auctionSubmitBid: builder.mutation<
      IAuctionSubmitBidRes,
      IAuctionSubmitBidDto
    >({
      query: ({ lotId, ...rest }) => ({
        url: `/auction/submit-bid/${lotId}`,
        method: "PATCH",
        data: { ...rest }
      }),
      async onQueryStarted({ userId, lotId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          dispatch?.(balanceApi.util.invalidateTags([TAGS.BALANCE]))

          dispatch(
            auctionAPI.util.updateQueryData(
              "getAuctionCurrent" as never,
              { userId } as never,
              (draft: any) => {
                const idx = draft.lots.findIndex(
                  (b: IAuctionCurrentLot) => String(b.lot._id) === String(lotId)
                )

                draft.lots[idx].lot.lastBidder = "myBid"
              }
            )
          )

          dispatch(
            missionsApi.util.invalidateTags([
              { type: TAGS.MISSIONS, id: "MY_PROGRESS" },
              { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }
            ])
          )
        } catch {
          //catch
        }
      }
    }),
    auctionBuyNow: builder.mutation<IAuctionBuyNowRes, IAuctionBuyNowDto>({
      query: ({ lotId, ...rest }) => ({
        url: `/auction/buy-now/${lotId}`,
        method: "PATCH",
        data: { ...rest }
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled

        try {
          dispatch(
            balanceEndpoints.util.updateQueryData(
              "getBalance",
              undefined,
              () => data.newBalance
            )
          )

          dispatch(
            elixirsAPI.util.invalidateTags([{ type: TAGS.ELIXIRS, id: "LIST" }])
          )
        } catch {
          // catch
        }
      }
    }),
    getUserAuctionStats: builder.query<IAuctionUserStatsRes, void>({
      query: () => ({
        url: `/auction/user-statistics`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.AUCTION, id: "USER_STATISTICS" }]
    })
  })
})

export const {
  useGetAuctionCurrentQuery,
  useGetAuctionHistoryQuery,
  useAuctionSubmitBidMutation,
  useAuctionBuyNowMutation,
  useGetUserAuctionStatsQuery
} = auctionEndpoints
